import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

export interface PageAnchor {
  /** Stable key for the anchor, unique within a page. */
  id: string;
  /** What the rail shows for it. */
  label: string;
}

interface RegisteredAnchor extends PageAnchor {
  element: HTMLElement;
}

export interface PageAnchorsContextInterface {
  /** Registered anchors in document order, recomputed whenever the page reorders them. */
  anchors: PageAnchor[];
  /** The anchor nearest the top of the viewport, so the rail can mark where you are. */
  activeAnchorId?: string;
  /**
   * Adds, updates or (with a `null` element) removes an anchor. Shaped as a ref callback so a
   * page registers by attaching it to the element it wants to scroll to.
   */
  registerAnchor: (anchor: PageAnchor, element: HTMLElement | null) => void;
  scrollToAnchor: (id: string) => void;
}

const PageAnchorsContext = createContext<PageAnchorsContextInterface | undefined>(undefined);

/** Breathing room between the pinned chrome and the card a jump lands on. */
const CHIN_GAP = 12;

/**
 * How far down the viewport the page's pinned chrome reaches — the person header and the toolbar
 * bar under it, each marked `data-sticky-chrome`. Measured per jump rather than hard-coded: the
 * bands stack differently per page, and the toolbar wraps to a second row on narrow windows.
 */
const stickyChinHeight = () =>
  [...document.querySelectorAll<HTMLElement>('[data-sticky-chrome]')].reduce(
    (lowest, element) => Math.max(lowest, element.getBoundingClientRect().bottom),
    0
  );

/** The longest a jump keeps correcting itself, however slowly the page's data arrives. */
const PIN_MAX_MS = 5000;
/** Inputs that mean the reader has taken over the scroll position; `mousedown` covers the scrollbar. */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown', 'mousedown'] as const;

/**
 * Scrolls the card to just under the pinned chrome and returns where it ended up. scroll-margin
 * rather than a scroll offset, so this works in any scroll container; `auto`, not `smooth`, since
 * browsers with smooth scrolling turned off ignore a smooth request outright.
 */
const alignToChrome = (target: HTMLElement) => {
  target.style.scrollMarginTop = `${stickyChinHeight() + CHIN_GAP}px`;
  target.scrollIntoView({ behavior: 'auto', block: 'start' });

  return target.getBoundingClientRect().top;
};

/** Document order, so a page that lets people drag cards around re-sorts the rail for free. */
const inDocumentOrder = (anchors: RegisteredAnchor[]) =>
  [...anchors].sort((a, b) =>
    a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  );

/**
 * Lets whatever page is open publish its on-page anchors — the cards on a profile, the sections
 * of a long form — so the section rail can list them and jump straight to one.
 *
 * Pages opt in through `usePageAnchor`; a page that registers nothing leaves the rail exactly as
 * it was. Nothing here reaches into page markup, which is what keeps the rail the only thing that
 * has to know this pattern exists.
 */
export const PageAnchorsProvider = ({ children }: { children: React.ReactNode }) => {
  const registry = useRef(new Map<string, RegisteredAnchor>());
  const pendingPublish = useRef<number | undefined>(undefined);
  const [anchors, setAnchors] = useState<PageAnchor[]>([]);
  const [activeAnchorId, setActiveAnchorId] = useState<string>();
  /** Bumped whenever the set of observed elements changes, so the observer below re-attaches. */
  const [elementEpoch, setElementEpoch] = useState(0);

  const publish = useCallback(() => {
    const ordered = inDocumentOrder([...registry.current.values()]);

    setAnchors((previous) => {
      const unchanged =
        previous.length === ordered.length &&
        previous.every((anchor, index) => {
          const next = ordered[index];
          return anchor.id === next.id && anchor.label === next.label;
        });

      return unchanged ? previous : ordered.map(({ id, label }) => ({ id, label }));
    });
  }, []);

  /**
   * Dragging a card to a new slot moves the element without remounting it, so no ref fires and
   * nothing tells us the order changed. Watching the document catches that; one publish per frame
   * keeps a busy page from re-sorting on every unrelated mutation.
   */
  useEffect(() => {
    if (!anchors.length || typeof window.MutationObserver === 'undefined') return undefined;

    const observer = new window.MutationObserver(() => {
      if (pendingPublish.current !== undefined) return;

      pendingPublish.current = window.requestAnimationFrame(() => {
        pendingPublish.current = undefined;
        publish();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();

      if (pendingPublish.current !== undefined) window.cancelAnimationFrame(pendingPublish.current);
      pendingPublish.current = undefined;
    };
  }, [anchors.length, publish]);

  const registerAnchor = useCallback(
    ({ id, label }: PageAnchor, element: HTMLElement | null) => {
      if (element) {
        const existing = registry.current.get(id);

        if (existing?.element === element && existing.label === label) return;

        registry.current.set(id, { id, label, element });

        // Only a new element needs re-observing; a renamed anchor is the same node.
        if (existing?.element === element) {
          publish();

          return;
        }
      } else {
        if (!registry.current.delete(id)) return;

        setActiveAnchorId((current) => (current === id ? undefined : current));
      }

      setElementEpoch((epoch) => epoch + 1);
      publish();
    },
    [publish]
  );

  const releasePin = useRef<(() => void) | undefined>(undefined);

  useEffect(() => () => releasePin.current?.(), []);

  /**
   * Cards above the target load as the jump brings them near the viewport, growing and pushing
   * the target away from where it landed. Puts it back whenever it moves, until the reader
   * scrolls for themselves or the time runs out. A card that cannot reach the top (the last one
   * on a short page) is compared to where it ended up, not to the top, so it is left alone.
   */
  const pinToTarget = useCallback((target: HTMLElement, landedAt: number) => {
    releasePin.current?.();

    if (typeof window.ResizeObserver === 'undefined') return;

    let lastTop = landedAt;

    const observer = new window.ResizeObserver(() => {
      if (Math.abs(target.getBoundingClientRect().top - lastTop) > 1) {
        lastTop = alignToChrome(target);
      }
    });

    observer.observe(document.body);
    if (target.parentElement) observer.observe(target.parentElement);

    const maxTimer = window.setTimeout(() => releasePin.current?.(), PIN_MAX_MS);
    const release = () => {
      observer.disconnect();
      window.clearTimeout(maxTimer);
      USER_SCROLL_EVENTS.forEach((event) => window.removeEventListener(event, release));
      if (releasePin.current === release) releasePin.current = undefined;
    };

    USER_SCROLL_EVENTS.forEach((event) =>
      window.addEventListener(event, release, { passive: true })
    );
    releasePin.current = release;
  }, []);

  const scrollToAnchor = useCallback(
    (id: string) => {
      const target = registry.current.get(id)?.element;

      if (!target) return;

      const landedAt = alignToChrome(target);
      setActiveAnchorId(id);
      pinToTarget(target, landedAt);
    },
    [pinToTarget]
  );

  /**
   * Highlight whichever anchor is closest to the top of the viewport. An IntersectionObserver
   * rather than a scroll listener because the anchors live in whatever scroll container the page
   * happens to use, and cards mount and unmount as data loads.
   */
  useEffect(() => {
    if (!registry.current.size || typeof window.IntersectionObserver === 'undefined')
      return undefined;

    const visible = new Set<string>();

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('data-page-anchor');

          if (!id) return;
          if (entry.isIntersecting) {
            visible.add(id);
          } else {
            visible.delete(id);
          }
        });

        const first = inDocumentOrder([...registry.current.values()]).find(({ id }) =>
          visible.has(id)
        );

        if (first) setActiveAnchorId(first.id);
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 }
    );

    registry.current.forEach(({ id, element }) => {
      element.setAttribute('data-page-anchor', id);
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [elementEpoch]);

  const value = useMemo(
    () => ({ anchors, activeAnchorId, registerAnchor, scrollToAnchor }),
    [activeAnchorId, anchors, registerAnchor, scrollToAnchor]
  );

  return <PageAnchorsContext.Provider value={value}>{children}</PageAnchorsContext.Provider>;
};

/**
 * Returns the anchors the open page has published. Falls back to an inert, empty registry when no
 * provider is mounted, so rails and pages stay renderable in isolation (tests, Storybook).
 */
export const usePageAnchors = (): PageAnchorsContextInterface => {
  const context = useContext(PageAnchorsContext);

  return (
    context ?? {
      anchors: [],
      activeAnchorId: undefined,
      registerAnchor: () => {},
      scrollToAnchor: () => {}
    }
  );
};

export default PageAnchorsContext;
